import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PawPrint, Heart, Sparkles, MapPin, Calendar, MessageCircle, ShieldCheck,
  Brain, Users, Baby, Plane, Star, ArrowRight, Quote, ChevronDown, Award,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";

const HERO_DOG = "https://customer-assets.emergentagent.com/job_pawsitive-app-1/artifacts/a11kswpe_IMG_3784.jpeg";
const TRAIN_IMG = "https://customer-assets.emergentagent.com/job_pawsitive-app-1/artifacts/1cv22ssi_IMG_8557.jpeg";
const PORTRAIT = "https://customer-assets.emergentagent.com/job_pawsitive-app-1/artifacts/r0mb3cpf_IMG_1384.jpeg";
const OWNER_PORTRAIT = "https://customer-assets.emergentagent.com/job_pawsitive-app-1/artifacts/ms1zqrwx_IMG_2140.jpeg";

const SERVICE_KEYS = [
  { key: "one_on_one", icon: Heart, color: "bg-primary/15 text-primary" },
  { key: "board_train", icon: ShieldCheck, color: "bg-secondary/40 text-secondary-foreground" },
  { key: "group", icon: Users, color: "bg-accent/40 text-accent-foreground" },
  { key: "train_trainer", icon: Baby, color: "bg-destructive/15 text-destructive" },
  { key: "behavioral", icon: Brain, color: "bg-primary/15 text-primary" },
  { key: "puppy", icon: Sparkles, color: "bg-secondary/40 text-secondary-foreground" },
  { key: "reactivity", icon: PawPrint, color: "bg-accent/40 text-accent-foreground" },
  { key: "pack_walks", icon: MapPin, color: "bg-destructive/15 text-destructive" },
];

const SERVICE_DETAILS = {
  one_on_one: {
    headline: { en: "Personalized in-home coaching for your dog and the whole family.",
                es: "Coaching personalizado en casa para tu perro y toda la familia." },
    bullets: {
      en: ["Custom 4–6 week plan based on your goals",
           "Weekly 1-hour sessions (in-home or virtual)",
           "Family training so everyone speaks the same language",
           "Homework + weekly progress check-ins"],
      es: ["Plan personalizado de 4–6 semanas según tus metas",
           "Sesiones semanales de 1h (en casa o virtuales)",
           "Entrenamiento familiar para hablar todos el mismo idioma",
           "Tarea + check-ins de progreso semanales"],
    },
    bestFor: { en: "Focused goals: leash manners, recall, calm at home, polite greetings.",
               es: "Metas concretas: correa, recall, calma en casa, saludos." },
    image: "https://images.unsplash.com/photo-1450778869180-41d0601e046e?w=900",
  },
  board_train: {
    headline: { en: "Your dog stays with us 2–4 weeks for an immersive reset.",
                es: "Tu perro vive con nosotros 2–4 semanas para un reset inmersivo." },
    bullets: {
      en: ["Daily structured training in a calm home setting",
           "Real-life exposure (cafés, parks, errands)",
           "Daily progress videos and notes",
           "Family transition coaching when they come home"],
      es: ["Entrenamiento diario estructurado en hogar tranquilo",
           "Exposición real (cafés, parques, mandados)",
           "Videos y notas de progreso diarios",
           "Coaching familiar para la transición a casa"],
    },
    bestFor: { en: "Deep behavior change in a short window. Great for reactive or anxious dogs.",
               es: "Cambio profundo en poco tiempo. Ideal para reactivos o ansiosos." },
    image: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=900",
  },
  group: {
    headline: { en: "Small classes (4–6 dogs) for social skills and consistency.",
                es: "Clases pequeñas (4–6 perros) para socializar y consistencia." },
    bullets: {
      en: ["6-week curriculum + community",
           "Sit, down, focus, leash, real-world distractions",
           "Friendly proofing with other dogs and people",
           "Optional graduation showcase 🎓"],
      es: ["Currículum de 6 semanas + comunidad",
           "Sentado, abajo, foco, correa, distracciones reales",
           "Proofing amigable con otros perros y personas",
           "Showcase de graduación opcional 🎓"],
    },
    bestFor: { en: "Social pups, families on a budget, or anyone who loves community learning.",
               es: "Perros sociables, presupuestos ajustados o quienes aman aprender en comunidad." },
    image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=900",
  },
  train_trainer: {
    headline: { en: "We teach your kids and family to BE the trainer.",
                es: "Enseñamos a tus niños y familia a SER los entrenadores." },
    bullets: {
      en: ["4 family sessions (kids 5+ welcome)",
           "Dog body-language workshop",
           "“Be a tree” + safe-greeting protocols for kids",
           "Custom training games the whole family can play"],
      es: ["4 sesiones familiares (niños desde 5 años)",
           "Taller de lenguaje corporal canino",
           "Protocolos seguros para niños («Be a tree»)",
           "Juegos de entrenamiento para toda la familia"],
    },
    bestFor: { en: "Families with kids, multi-handler homes, lifelong bonds.",
               es: "Familias con niños, casas con varios responsables, vínculos de por vida." },
    image: "https://images.pexels.com/photos/4148853/pexels-photo-4148853.jpeg?w=900",
  },
  behavioral: {
    headline: { en: "Custom protocols for tougher cases — fear, aggression, anxiety.",
                es: "Protocolos personalizados para casos difíciles — miedo, agresión, ansiedad." },
    bullets: {
      en: ["Behavior assessment + safety plan",
           "Counter-conditioning + desensitization",
           "Vet/medication coordination when helpful",
           "Weekly coaching + progress tracking"],
      es: ["Evaluación + plan de seguridad",
           "Contracondicionamiento + desensibilización",
           "Coordinación con veterinario/medicación si ayuda",
           "Coaching semanal + seguimiento de progreso"],
    },
    bestFor: { en: "Bites in history, leash reactivity, separation issues, fearful dogs.",
               es: "Historial de mordidas, reactividad, ansiedad por separación, miedo." },
    image: "https://images.unsplash.com/photo-1561037404-61cd46aa615b?w=900",
  },
  puppy: {
    headline: { en: "Set the foundations right — 8 to 16 weeks is magic time.",
                es: "Sienta bases sólidas — de 8 a 16 semanas es la edad mágica." },
    bullets: {
      en: ["Potty + crate + sleep routines that actually work",
           "Healthy socialization (people, places, sounds)",
           "Bite inhibition + handling tolerance",
           "Name, recall, sit, settle — the joyful basics"],
      es: ["Pipí, kennel y sueño con rutinas que sí funcionan",
           "Socialización sana (gente, lugares, sonidos)",
           "Inhibición de mordida + tolerancia al manejo",
           "Nombre, recall, sentado, settle — básicos felices"],
    },
    bestFor: { en: "New puppy parents who want to do it right from day one.",
               es: "Familias con cachorro nuevo que quieren hacerlo bien desde el día uno." },
    image: "https://images.unsplash.com/photo-1591946614720-90a587da4a36?w=900",
  },
  reactivity: {
    headline: { en: "Specialized 8–12 week program for reactive and anxious dogs.",
                es: "Programa especializado de 8–12 semanas para perros reactivos o ansiosos." },
    bullets: {
      en: ["Threshold work + LAT (Look-At-That) protocol",
           "Engage / Disengage games",
           "Real-world walks at safe distances",
           "Family scripts so it sticks"],
      es: ["Trabajo de umbral + protocolo LAT (Look-At-That)",
           "Juegos Engage / Disengage",
           "Paseos reales a distancia segura",
           "Guiones familiares para que se mantenga"],
    },
    bestFor: { en: "Lunging, barking, hiding, or freezing on walks — there's a way through.",
               es: "Tirones, ladridos, esconderse o quedarse helado en paseos — sí hay salida." },
    image: "https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=900",
  },
  pack_walks: {
    headline: { en: "Calm, structured group walks that build confidence and social skills.",
                es: "Caminatas grupales tranquilas y estructuradas que construyen confianza y habilidades sociales." },
    bullets: {
      en: ["Small, balanced packs led with calm energy",
           "Real-world exposure: sights, sounds, and other dogs",
           "Builds focus, recall, and polite leash skills",
           "Tons of physical + mental drainage in one outing"],
      es: ["Manadas pequeñas y balanceadas con energía tranquila",
           "Exposición real: sonidos, vistas y otros perros",
           "Construye foco, recall y modales en correa",
           "Mucho desgaste físico y mental en una salida"],
    },
    bestFor: { en: "High-energy dogs, social pups, or any dog who needs a confident pack experience.",
               es: "Perros con mucha energía, sociables, o cualquiera que necesite una experiencia de manada confiada." },
    image: "https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=900",
  },
};

const CITY_DETAILS = {
  // United States — major metros (Charlotte = home base)
  Charlotte: { country: "US", flag: "🇺🇸", note: { en: "Delicia's home base! In-person sessions across Charlotte metro and the Carolinas.", es: "¡La base de Delicia! Sesiones en persona en Charlotte y las Carolinas." } },
  "Los Angeles": { country: "US", flag: "🇺🇸", note: { en: "Apartment dogs, busy schedules, beach-walk dreams — and all of LA County.", es: "Perros de departamento, agendas ocupadas, paseos en la playa — y todo LA County." } },
  "New York": { country: "US", flag: "🇺🇸", note: { en: "City-dog mastery: subways, sidewalks, doormen, dog parks — all five boroughs + NJ commuters.", es: "Maestría urbana: metro, banquetas, doormen y parques — los cinco barrios y NJ." } },
  Atlanta: { country: "US", flag: "🇺🇸", note: { en: "Trail-loving families and rescue dogs — metro Atlanta + surrounding counties.", es: "Familias de senderos y perros rescatados — metro Atlanta + condados alrededor." } },
  Dallas: { country: "US", flag: "🇺🇸", note: { en: "DFW + Fort Worth and the surrounding North Texas region.", es: "DFW + Fort Worth y todo el norte de Texas." } },
  Houston: { country: "US", flag: "🇺🇸", note: { en: "Big-energy pups in big-energy heat. Greater Houston region covered.", es: "Perros con mucha energía en calor texano. Cubrimos todo el área de Houston." } },
  Miami: { country: "US", flag: "🇺🇸", note: { en: "Bilingual sessions, beachy walks — Miami-Dade and Broward counties.", es: "Sesiones bilingües, paseos en la playa — Miami-Dade y Broward." } },
  Chicago: { country: "US", flag: "🇺🇸", note: { en: "City + suburbs (Chicagoland). Winter-resilient dogs and cozy indoor enrichment.", es: "Ciudad + suburbios (Chicagoland). Perros resistentes al invierno y enriquecimiento bajo techo." } },
  Boston: { country: "US", flag: "🇺🇸", note: { en: "Greater Boston + Cambridge. Smart, social pups thrive here.", es: "Greater Boston + Cambridge. Perros sociables y listos prosperan aquí." } },
  "Washington DC": { country: "US", flag: "🇺🇸", note: { en: "DC + NoVA + MD suburbs. Embassy-row pups welcome.", es: "DC + NoVA + suburbios de MD. Bienvenidos los perros de la zona diplomática." } },
  "San Francisco": { country: "US", flag: "🇺🇸", note: { en: "Bay Area: SF + Oakland + Peninsula. City-dog savvy training.", es: "Bay Area: SF + Oakland + Peninsula. Entrenamiento urbano." } },
  Seattle: { country: "US", flag: "🇺🇸", note: { en: "Rainy-day games, indoor enrichment, café-friendly manners — King + Snohomish counties.", es: "Juegos para días lluviosos, enriquecimiento y café — King y Snohomish." } },
  Denver: { country: "US", flag: "🇺🇸", note: { en: "Mountain pups, hiking recall, altitude-adjusted training — Front Range + Boulder.", es: "Perros de montaña, recall en senderos — Front Range + Boulder." } },
  Austin: { country: "US", flag: "🇺🇸", note: { en: "Music-festival energy meets calm canine confidence. Greater Austin covered.", es: "Energía de festival con confianza tranquila. Cubrimos todo el área de Austin." } },
  Nashville: { country: "US", flag: "🇺🇸", note: { en: "Music City pups + surrounding Middle Tennessee.", es: "Perros de Music City + todo Middle Tennessee." } },
  Philadelphia: { country: "US", flag: "🇺🇸", note: { en: "Philly + South Jersey + Main Line. Brotherly-love dog training.", es: "Philly + South Jersey + Main Line. Entrenamiento con amor fraternal." } },
  "San Diego": { country: "US", flag: "🇺🇸", note: { en: "Beachy SD + North County. Sun, sand, and recall games.", es: "SD playero + North County. Sol, arena y juegos de recall." } },
  "Las Vegas": { country: "US", flag: "🇺🇸", note: { en: "Strip-side or suburb — desert-savvy training keeps pups cool and confident.", es: "Strip o suburbio — entrenamiento desértico para perros tranquilos." } },
  Portland: { country: "US", flag: "🇺🇸", note: { en: "Portland metro + outer suburbs. Rain-or-shine training.", es: "Portland metro + suburbios. Entrenamiento llueva o truene." } },
  Tampa: { country: "US", flag: "🇺🇸", note: { en: "Tampa Bay + St. Pete + Clearwater. Water-loving pups welcome.", es: "Tampa Bay + St. Pete + Clearwater. Perros amantes del agua bienvenidos." } },
  Orlando: { country: "US", flag: "🇺🇸", note: { en: "Theme-park city pups + surrounding Central Florida.", es: "Perros de la ciudad de los parques + Central Florida." } },
  // Mexico
  Monterrey: { country: "MX", flag: "🇲🇽", note: { en: "Familias activas, perros enérgicos y mucho corazón.", es: "Familias activas, perros enérgicos y mucho corazón." } },
  CDMX: { country: "MX", flag: "🇲🇽", note: { en: "Apartment-dog champions, Roma/Condesa parks, bilingual support.", es: "Maestros de perros de depa, parques de Roma/Condesa, soporte bilingüe." } },
  Guadalajara: { country: "MX", flag: "🇲🇽", note: { en: "Tradición + entrenamiento moderno. Paseos urbanos + parques.", es: "Tradición + entrenamiento moderno. Paseos urbanos + parques." } },
  "Cancún": { country: "MX", flag: "🇲🇽", note: { en: "Beach training, vacation pups, expat families welcome.", es: "Entrenamiento en playa, perros vacacionistas, familias expat bienvenidas." } },
  Cancun: { country: "MX", flag: "🇲🇽", note: { en: "Beach training, vacation pups, expat families welcome.", es: "Entrenamiento en playa, perros vacacionistas, familias expat bienvenidas." } },
  Tijuana: { country: "MX", flag: "🇲🇽", note: { en: "Cross-border families, bilingual sessions, all temperaments.", es: "Familias bi-nacionales, sesiones bilingües, todos los temperamentos." } },
  "Querétaro": { country: "MX", flag: "🇲🇽", note: { en: "Tranquilidad y rutinas familiares — tu perro va a amarlo.", es: "Tranquilidad y rutinas familiares — tu perro va a amarlo." } },
  Puebla: { country: "MX", flag: "🇲🇽", note: { en: "Histórico, familiar, paseos largos. Aquí el perro se pone zen.", es: "Histórico, familiar, paseos largos. Aquí el perro se pone zen." } },
  "Mérida": { country: "MX", flag: "🇲🇽", note: { en: "Yucatán heat-savvy training, expat-friendly, bilingual.", es: "Entrenamiento adaptado al calor yucateco, expat-friendly, bilingüe." } },
  León: { country: "MX", flag: "🇲🇽", note: { en: "Bajío families and active pups. We come to you.", es: "Familias del Bajío y perros activos. Vamos a ti." } },
};

const WHY_DETAILS = {
  behavior: {
    icon: Brain,
    title: { en: "Behavior, not obedience", es: "Comportamiento, no obediencia" },
    headline: {
      en: "We start with the dog behind the behavior — not a list of commands.",
      es: "Partimos del perro detrás del comportamiento — no de una lista de comandos.",
    },
    bullets: {
      en: [
        "Behavior assessment before any rule is added",
        "Calm, balanced energy is the foundation of every session",
        "We address the root cause — anxiety, fear, over-arousal — not symptoms",
        "Communication first, structure with purpose",
      ],
      es: [
        "Evaluación del comportamiento antes de añadir cualquier regla",
        "La energía tranquila y balanceada es la base de cada sesión",
        "Trabajamos la raíz — ansiedad, miedo, sobre-excitación — no los síntomas",
        "Comunicación primero, estructura con propósito",
      ],
    },
    quote: {
      en: '"Understand first. Train second."',
      es: '«Entender primero. Entrenar después.»',
    },
  },
  family: {
    icon: Users,
    title: { en: "The whole family trains", es: "Toda la familia entrena" },
    headline: {
      en: "Consistency from every human in the home is where magic actually happens.",
      es: "La consistencia de cada humano en casa es donde la magia ocurre.",
    },
    bullets: {
      en: [
        "Kids learn safe, calm body language with their dog",
        "Partners and grandparents get the same scripts so everyone is aligned",
        "Family training games make practice feel like play",
        "Built around your real routines — meals, school, bedtime",
      ],
      es: [
        "Los niños aprenden lenguaje corporal seguro y tranquilo con su perro",
        "Pareja y abuelos reciben los mismos guiones — todos alineados",
        "Juegos familiares para que practicar se sienta jugar",
        "Diseñado alrededor de tu rutina real — comidas, escuela, hora de dormir",
      ],
    },
    quote: {
      en: '"Kids, partners, grandparents — everyone speaks the same language."',
      es: '«Niños, parejas, abuelos — todos hablan el mismo idioma.»',
    },
  },
  coverage: {
    icon: MapPin,
    title: { en: "Wherever you live", es: "Donde vivas" },
    headline: {
      en: "Active across the U.S. and Mexico — and if there's no trainer near you, we'll bring one.",
      es: "Activos en EE. UU. y México — y si no hay un entrenador cerca, llevamos uno.",
    },
    bullets: {
      en: [
        "Approved trainers across 15+ cities and growing",
        "In-home, virtual, or hybrid programs to fit your reality",
        "Bilingual sessions (English / Spanish) available in many cities",
        "If we don't have a local match, our traveling trainer comes to you",
      ],
      es: [
        "Entrenadores aprobados en 15+ ciudades y creciendo",
        "Programas en casa, virtuales o híbridos para tu realidad",
        "Sesiones bilingües (inglés / español) en muchas ciudades",
        "Si no hay match local, nuestra entrenadora viajera va a ti",
      ],
    },
    quote: {
      en: '"No local trainer? We bring one to you."',
      es: '«¿Sin entrenador local? Llevamos uno a ti.»',
    },
  },
};

const FAQ_ITEMS_EN = [
  { q: "What does 'psychology-based' actually mean?",
    a: "We start with how your dog feels and learns. We use positive reinforcement and behavior science instead of force or fear. The goal isn't a robot dog — it's a calm, confident family member." },
  { q: "What happens if there's no trainer in my area yet?",
    a: "We'll bring one to you! We send an Oh My Dog! trainer to your city for an intensive program, plus virtual follow-ups for your family." },
  { q: "Do you train kids too?",
    a: "Yes — kids are part of the training. Our 'train-the-trainer' sessions teach children safe, calm body language and how to be confident with their dog." },
  { q: "How long until we see results?",
    a: "Most families see meaningful changes in 2–4 weeks. Reactivity and anxiety work tends to be longer (6–12 weeks)." },
  { q: "Can we do virtual sessions?",
    a: "Absolutely. Many of our trainers do hybrid programs — in-home + virtual coaching between sessions." },
];
const FAQ_ITEMS_ES = [
  { q: "¿Qué significa 'basado en psicología'?",
    a: "Partimos de cómo se siente y aprende tu perro. Usamos refuerzo positivo y ciencia del comportamiento, sin fuerza ni miedo. La meta no es un perro robot, es un compañero familiar tranquilo y confiado." },
  { q: "¿Qué pasa si no hay entrenador en mi zona?",
    a: "¡Llevamos uno a ti! Enviamos un entrenador Oh My Dog! a tu ciudad para un programa intensivo, más seguimientos virtuales para la familia." },
  { q: "¿También entrenan a los niños?",
    a: "Sí — los niños son parte del entrenamiento. Las sesiones 'train-the-trainer' enseñan lenguaje corporal seguro y cómo estar tranquilos con su perro." },
  { q: "¿En cuánto tiempo se ven resultados?",
    a: "Muchas familias notan cambios reales en 2–4 semanas. Reactividad y ansiedad suelen tomar 6–12 semanas." },
  { q: "¿Hay sesiones virtuales?",
    a: "Sí. Muchos entrenadores hacen programas híbridos — en casa más coaching virtual entre sesiones." },
];

const TESTIMONIAL_EN = [
  { name: "The Patel Family · Phoenix",
    body: "Luna went from barking at every doorbell to settling on her mat in 3 weeks. The kids LOVE 'training time' now.",
    rating: 5 },
  { name: "Familia Hernández · CDMX",
    body: "Mochi era tan ansioso… hoy cruza el parque sin reaccionar. Sarah y todo el equipo cambiaron nuestras vidas.",
    rating: 5 },
  { name: "Jordan & Alex · Brooklyn",
    body: "We tried 3 trainers before Oh My Dog!. The match alone made the difference. Best money we've ever spent.",
    rating: 5 },
];

export default function Landing() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [serviceModal, setServiceModal] = useState(null);
  const [cityModal, setCityModal] = useState(null);
  const [whyModal, setWhyModal] = useState(null);

  const FAQ_ITEMS = lang === "es" ? FAQ_ITEMS_ES : FAQ_ITEMS_EN;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative gradient-hero" data-testid="hero-section">
        <div className="absolute inset-0 paw-pattern opacity-70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6 fade-up">
            <Badge className="rounded-full bg-background/80 backdrop-blur text-foreground border border-border px-4 py-1.5 text-xs uppercase tracking-widest font-bold">
              <PawPrint className="w-3.5 h-3.5 mr-1.5" />
              {t("brand.tag")} · 🇺🇸 🇲🇽
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              {t("hero.title")}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {t("hero.sub")}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                size="lg"
                className="rounded-full font-bold text-base px-7 py-6 shadow-lg lift"
                onClick={() => navigate("/request-training")}
                data-testid="hero-cta-primary"
              >
                {t("hero.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full font-bold text-base px-7 py-6 bg-background/70 backdrop-blur"
                onClick={() => scrollTo("services")}
                data-testid="hero-cta-secondary"
              >
                {lang === "es" ? "Ver servicios" : "See services"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-6 pt-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" />Vetted & certified</span>
              <span className="flex items-center gap-2"><Heart className="w-4 h-4 text-destructive" />Family-friendly</span>
              <span className="flex items-center gap-2"><Brain className="w-4 h-4 text-primary" />Science-backed</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "150ms" }}>
            <div className="relative">
              <img src={HERO_DOG} alt="Family with dog"
                className="rounded-[2rem] shadow-2xl w-full aspect-square object-cover float" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE / KEY PHRASES */}
      <section className="border-y border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-around gap-x-8 gap-y-2 text-xs sm:text-sm font-bold uppercase tracking-widest text-muted-foreground">
          <span>🐾 {t("phrase.everyDog")}</span>
          <span>✨ {t("phrase.understanding")}</span>
          <span>💚 {t("phrase.chaos")}</span>
          <span>🎯 {t("phrase.match")}</span>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section id="how" className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
            {t("section.different.kicker")}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">
            {t("section.different.title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {t("section.different.sub")}
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { key: "behavior", icon: Brain, title: lang === "es" ? "Comportamiento, no obediencia" : "Behavior, not obedience",
              body: lang === "es"
                ? "Tu perro no necesita más comandos — necesita ser entendido. Trabajamos la causa, no el síntoma."
                : "Your dog doesn't need more commands — they need to be understood. We work the cause, not the symptom." },
            { key: "family", icon: Users, title: lang === "es" ? "Toda la familia entrena" : "The whole family trains",
              body: lang === "es"
                ? "Niños, abuelos, parejas — todos aprenden el mismo lenguaje. Consistencia = magia."
                : "Kids, partners, grandparents — everyone learns the same language. Consistency = magic." },
            { key: "coverage", icon: MapPin, title: lang === "es" ? "Donde vivas" : "Wherever you live",
              body: lang === "es"
                ? "EE.UU. y México. Y si no tenemos a nadie cerca, llevamos uno a ti."
                : "U.S. + Mexico. If we don't have someone near you, we'll bring one to you." },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => setWhyModal(item.key)}
              className="text-left"
              data-testid={`why-${item.key}`}
            >
              <Card className="rounded-3xl border-border/60 soft-shadow lift bg-card cursor-pointer hover:border-primary/60 h-full">
                <CardContent className="p-7">
                  <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.body}</p>
                  <p className="text-xs font-bold uppercase tracking-widest text-primary mt-4 flex items-center gap-1">
                    {lang === "es" ? "Ver más" : "Learn more"} <ArrowRight className="w-3 h-3" />
                  </p>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      </section>

      {/* PSYCHOLOGY-BASED EXPLAINED */}
      <section className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
              {t("section.psych.kicker")}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-5">
              {t("section.psych.title")}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
              {t("section.psych.sub")}
            </p>
            <ul className="space-y-3">
              {[
                lang === "es" ? "Refuerzo positivo & ciencia del comportamiento" : "Positive reinforcement & behavior science",
                lang === "es" ? "Cero castigo, cero fuerza" : "Zero punishment, zero force",
                lang === "es" ? "Lectura de lenguaje corporal canino" : "Canine body-language literacy",
                lang === "es" ? "Plan diseñado a tu rutina familiar" : "Plan designed around your family routine",
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center mt-0.5 shrink-0">
                    <PawPrint className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-base">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={TRAIN_IMG} alt="Trainer with dog"
              className="rounded-[2rem] shadow-xl w-full aspect-square object-cover" />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">
            {t("services.title")}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">{t("services.sub")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICE_KEYS.map((s) => (
            <button
              key={s.key}
              onClick={() => setServiceModal(s.key)}
              className="text-left"
              data-testid={`service-${s.key}`}
            >
              <Card className="rounded-3xl border-border/60 soft-shadow lift cursor-pointer h-full hover:border-primary/60">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${s.color}`}>
                    <s.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-lg font-bold mb-1.5">{t(`service.${s.key}`)}</h3>
                  <p className="text-sm text-muted-foreground">
                    {lang === "es"
                      ? "Plan personalizado, técnicas positivas y soporte familiar."
                      : "Personalized plan, positive methods, and family support."}
                  </p>
                  <p className="text-xs font-bold uppercase tracking-widest text-primary mt-4 flex items-center gap-1">
                    {lang === "es" ? "Ver detalles" : "Learn more"} <ArrowRight className="w-3 h-3" />
                  </p>
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      </section>

      {/* FOR FAMILIES / KIDS */}
      <section id="families" className="py-20 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <img src={PORTRAIT} alt="Dog portrait" className="rounded-[2rem] shadow-xl w-full aspect-square object-cover" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
              {lang === "es" ? "Para familias" : "For families"}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-5">
              {lang === "es" ? "Niños y perros, mejor juntos." : "Kids and dogs, better together."}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-5">
              {lang === "es"
                ? "Las sesiones train-the-trainer enseñan a los niños a leer a su perro y comunicarse con calma. La conexión es para toda la vida."
                : "Train-the-trainer sessions teach kids to read their dog and communicate calmly. The bond becomes lifelong."}
            </p>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full font-semibold"
              onClick={() => navigate("/request-training")}
              data-testid="families-cta"
            >
              {t("nav.contact")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>

      {/* MATCHING + COVERAGE — redesigned full-width */}
      <section id="trainers" className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
            {lang === "es" ? "Cómo funciona" : "How it works"}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">{t("match.title")}</h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{t("match.sub")}</p>
        </div>

        {/* 5-step horizontal flow */}
        <div className="relative">
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-border/60" />
          <ol className="relative grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: MapPin, label: lang === "es" ? "País & ciudad" : "Country & city",
                tip: lang === "es" ? "Empezamos por dónde vives." : "We start with where you live." },
              { icon: Sparkles, label: lang === "es" ? "Especialidad" : "Specialty",
                tip: lang === "es" ? "Reactividad, cachorros, familia…" : "Reactivity, puppies, family…" },
              { icon: Calendar, label: lang === "es" ? "Disponibilidad" : "Availability",
                tip: lang === "es" ? "Que coincida con tu rutina." : "Matching your real schedule." },
              { icon: Star, label: lang === "es" ? "Calificación" : "Rating",
                tip: lang === "es" ? "Solo entrenadores aprobados." : "Only approved trainers." },
              { icon: Plane, label: lang === "es" ? "¿Sin local? Llevamos uno" : "No local? We bring one",
                tip: lang === "es" ? "Entrenador viajero a tu ciudad." : "Traveling trainer to your city." },
            ].map((step, i) => (
              <li key={i} className="relative bg-card border border-border/60 rounded-3xl p-5 soft-shadow lift text-center">
                <div className="relative w-14 h-14 mx-auto mb-3">
                  <span className="absolute inset-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-black text-xl shadow-md">
                    {i + 1}
                  </span>
                </div>
                <step.icon className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="font-display font-bold text-base mb-1">{step.label}</p>
                <p className="text-xs text-muted-foreground leading-snug">{step.tip}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Coverage card — full width below */}
        <div className="mt-10 bg-card border border-border/60 rounded-3xl p-6 sm:p-8 soft-shadow">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h3 className="font-display text-2xl font-bold">{t("coverage.title")}</h3>
              <p className="text-sm text-muted-foreground">{t("coverage.sub")}</p>
            </div>
            <Badge className="rounded-full bg-secondary text-secondary-foreground">
              <PawPrint className="w-3 h-3 mr-1" />
              {lang === "es" ? "2 países · 30+ ciudades" : "2 countries · 30+ cities"}
            </Badge>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">🇺🇸 United States</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "Charlotte", "Los Angeles", "New York", "Atlanta", "Dallas", "Houston",
                  "Miami", "Chicago", "Boston", "Washington DC", "San Francisco", "Seattle",
                  "Denver", "Austin", "Nashville", "Philadelphia", "San Diego", "Las Vegas",
                  "Portland", "Tampa", "Orlando",
                ].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCityModal(c)}
                    className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold hover:bg-primary hover:text-primary-foreground transition lift"
                    data-testid={`city-chip-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">🇲🇽 México</p>
              <div className="flex flex-wrap gap-2">
                {["Monterrey", "CDMX", "Guadalajara", "Cancún", "Tijuana", "Querétaro", "Puebla", "Mérida", "León"].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCityModal(c)}
                    className="px-3 py-1.5 rounded-full bg-accent/30 text-accent-foreground text-xs font-bold hover:bg-accent transition lift"
                    data-testid={`city-chip-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* HERO disclaimer — we meet you anywhere */}
          <div className="mt-6 rounded-2xl gradient-teal p-5 sm:p-6 text-white shadow-lg">
            <div className="flex items-start gap-3">
              <Plane className="w-6 h-6 mt-0.5 shrink-0" />
              <div>
                <p className="font-display font-black text-lg sm:text-xl mb-1">
                  {lang === "es"
                    ? "Llegamos a donde estés. Punto."
                    : "We meet you wherever you're at. Period."}
                </p>
                <p className="text-sm text-white/95 leading-relaxed">
                  {lang === "es"
                    ? "No vemos tu ciudad arriba? No te preocupes — estos son solo nuestros hubs principales. Cubrimos toda la región alrededor y, si hace falta, viajamos a ti. Solicita y nos coordinamos."
                    : "Don't see your city above? No worries — these are just our most-common hubs. We cover the entire surrounding region and, if needed, we'll travel to you. Just request training and we'll figure it out together."}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 p-4 rounded-2xl bg-secondary/40 border border-border/60 flex items-start gap-3">
            <PawPrint className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <p className="text-sm">
              <span className="font-bold">{lang === "es" ? "¿Sin hub local?" : "No local hub?"}</span>{" "}
              {lang === "es"
                ? "Enviamos a Delicia (o a un entrenador Oh My Dog!) a tu ciudad para un programa intensivo + seguimiento virtual."
                : "We'll fly Delicia (or an Oh My Dog! trainer) to your city for an intensive program + virtual follow-ups."}
            </p>
          </div>
          <p className="mt-3 text-xs text-muted-foreground text-center">
            <MapPin className="w-3 h-3 inline mr-1" />
            {lang === "es"
              ? "Las ciudades arriba son hubs principales — atendemos toda la región alrededor. Selecciona la más cercana a ti."
              : "Cities above are coverage hubs — we serve every area around them. Pick the one closest to you."}
          </p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">{t("testimonials.title")}</h2>
          <p className="text-base sm:text-lg text-muted-foreground">{t("testimonials.sub")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIAL_EN.map((tt, i) => (
            <Card key={i} className="rounded-3xl border-border/60 soft-shadow bg-card lift" data-testid={`testimonial-${i}`}>
              <CardContent className="p-7">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: tt.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-base leading-relaxed mb-4">"{tt.body}"</p>
                <p className="text-sm font-bold text-primary">{tt.name}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-secondary/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-center mb-10">
            {t("faq.title")}
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQ_ITEMS.map((it, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-card border border-border/60 rounded-2xl px-5 soft-shadow">
                <AccordionTrigger className="font-display text-left font-bold text-base py-5 hover:no-underline">
                  {it.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {it.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ABOUT THE OWNER */}
      <section id="about-owner" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="relative lg:sticky lg:top-24">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-accent/30 -rotate-2" />
              <img
                src={OWNER_PORTRAIT}
                alt="Delicia, founder of Oh My Dog! Training"
                className="relative rounded-[2rem] shadow-2xl w-full aspect-[4/5] object-cover"
                data-testid="owner-photo"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
              {lang === "es" ? "Sobre Delicia" : "About Delicia"}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-5 font-display">
              {lang === "es" ? "Hola, soy " : "Hi, I'm "}
              <span className="text-primary">Delicia</span>
            </h2>

            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              {[
                { icon: Award, label: lang === "es" ? "En perros desde" : "In dogs since", v: "2017" },
                { icon: PawPrint, label: lang === "es" ? "Filosofía" : "Philosophy", v: lang === "es" ? "Comunicación" : "Communication" },
                { icon: Heart, label: lang === "es" ? "Sus pups" : "Her pups", v: "Baily & Cracker" },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl bg-card border border-border/60 p-4 text-center soft-shadow">
                  <s.icon className="w-5 h-5 text-primary mx-auto mb-1.5" />
                  <p className="font-bold text-base">{s.v}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="space-y-4 text-base text-muted-foreground leading-relaxed mb-6">
              <p>
                Delicia has been in the dog industry since <span className="font-bold text-foreground">2017</span>, but her path into the field is what truly sets her apart. After moving from Texas to Charlotte, North Carolina with her husband and their pups, she made a career pivot from working as a <span className="font-bold text-foreground">pediatric speech therapist</span> into the world of dogs.
              </p>
              <p>
                What started as a role with a local dog-walking company quickly became something more. She realized that her background working with children — understanding behavior, communication, patience, and emotional awareness — translated seamlessly into working with dogs. <span className="italic text-foreground">This convergence of child development and dog behavior became the foundation of her approach.</span>
              </p>
              <p>
                Training her own pup, <span className="font-bold text-foreground">Baily</span>, sparked a deeper passion and led her to join a professional training company. There she recognized her drive for more thoughtful, communication-based, and effective training methods. She went on to start her own business, <span className="font-bold text-primary">Oh My Dog!</span>, offering boarding, daycare, and training while continuing to study and refine her craft.
              </p>
              <p>
                In early 2024, Delicia moved west to work hands-on with her mentor in California, expanding her skillset and deepening her philosophy. She later joined a pack walking company, gaining yet another mentor and further developing her ability to read dogs, understand pack dynamics, and lead with calm, clear, balanced energy.
              </p>
              <p>
                Today, those experiences shape the core of her training: <span className="font-bold text-foreground">communication first, relationship always, and structure with purpose.</span>
              </p>
              <p>
                At home, Delicia and her husband share life with their lab mix <span className="font-bold text-foreground">Baily</span> and their newest rescue pup <span className="font-bold text-foreground">Cracker</span> — both of whom continue to inspire the heart behind Oh My Dog! Training every single day.
              </p>
            </div>

            <div className="rounded-2xl bg-background border border-border/60 p-5 soft-shadow">
              <Quote className="w-5 h-5 text-primary mb-2" />
              <p className="italic text-base leading-relaxed font-semibold">
                "Communication first, relationship always, and structure with purpose."
              </p>
              <p className="text-xs text-muted-foreground mt-2">— Delicia, founder</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="rounded-[2.5rem] gradient-hero p-10 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 paw-pattern opacity-50" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">{t("cta.final.title")}</h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">{t("cta.final.sub")}</p>
            <Button
              size="lg"
              className="rounded-full font-bold text-base px-8 py-6 shadow-xl lift"
              onClick={() => navigate("/request-training")}
              data-testid="final-cta-btn"
            >
              {t("hero.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>
      {/* WHY DETAIL MODAL */}
      <Dialog open={!!whyModal} onOpenChange={(v) => !v && setWhyModal(null)}>
        <DialogContent className="rounded-3xl max-w-xl" data-testid="why-modal">
          {whyModal && (() => {
            const d = WHY_DETAILS[whyModal];
            const Icon = d.icon;
            return (
              <>
                <DialogHeader>
                  <div className="w-14 h-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mb-2">
                    <Icon className="w-7 h-7" />
                  </div>
                  <DialogTitle className="font-display text-3xl font-black tracking-tight">
                    {d.title[lang] || d.title.en}
                  </DialogTitle>
                  <DialogDescription className="text-base text-foreground/80 mt-2">
                    {d.headline[lang] || d.headline.en}
                  </DialogDescription>
                </DialogHeader>
                <ul className="space-y-2 mt-3">
                  {(d.bullets[lang] || d.bullets.en).map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <PawPrint className="w-4 h-4 text-primary mt-1 shrink-0" />
                      <span className="text-sm">{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="rounded-2xl bg-secondary/30 border border-border/60 p-4 mt-3">
                  <Quote className="w-4 h-4 text-primary mb-1" />
                  <p className="italic text-sm font-semibold">{d.quote[lang] || d.quote.en}</p>
                </div>
                <Button
                  className="rounded-full font-bold w-full mt-3"
                  onClick={() => {
                    setWhyModal(null);
                    navigate("/request-training");
                  }}
                  data-testid="why-modal-cta"
                >
                  {t("hero.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* SERVICE DETAIL MODAL */}
      <Dialog open={!!serviceModal} onOpenChange={(v) => !v && setServiceModal(null)}>
        <DialogContent className="rounded-3xl max-w-2xl p-0 overflow-hidden" data-testid="service-modal">
          {serviceModal && (() => {
            const d = SERVICE_DETAILS[serviceModal];
            return (
              <>
                <img src={d.image} alt={t(`service.${serviceModal}`)}
                  className="w-full aspect-[16/8] object-cover" />
                <div className="p-6 sm:p-8">
                  <DialogHeader>
                    <DialogTitle className="font-display text-3xl font-black tracking-tight">
                      {t(`service.${serviceModal}`)}
                    </DialogTitle>
                    <DialogDescription className="text-base text-foreground/80 mt-2">
                      {d.headline[lang] || d.headline.en}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
                      {lang === "es" ? "Qué incluye" : "What's included"}
                    </p>
                    <ul className="space-y-2">
                      {(d.bullets[lang] || d.bullets.en).map((b, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <PawPrint className="w-4 h-4 text-primary mt-1 shrink-0" />
                          <span className="text-sm">{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-5 p-4 rounded-2xl bg-secondary/30 border border-border/60">
                    <p className="text-xs font-bold uppercase tracking-widest text-primary mb-1">
                      {lang === "es" ? "Ideal para" : "Best for"}
                    </p>
                    <p className="text-sm">{d.bestFor[lang] || d.bestFor.en}</p>
                  </div>
                  <Button
                    className="rounded-full font-bold w-full mt-6"
                    onClick={() => {
                      setServiceModal(null);
                      navigate("/request-training");
                    }}
                    data-testid="service-modal-cta"
                  >
                    {t("hero.cta")} <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* CITY DETAIL MODAL */}
      <Dialog open={!!cityModal} onOpenChange={(v) => !v && setCityModal(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="city-modal">
          {cityModal && (() => {
            const d = CITY_DETAILS[cityModal] || { country: "US", flag: "🌎", note: { en: "We cover this city.", es: "Cubrimos esta ciudad." } };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
                    <span className="text-2xl">{d.flag}</span> {cityModal}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {d.country === "US" ? "United States" : "México"}
                  </DialogDescription>
                </DialogHeader>
                <p className="text-base text-foreground/80 leading-relaxed">
                  {d.note[lang] || d.note.en}
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="rounded-xl bg-primary/10 p-3 text-center">
                    <p className="text-xs font-bold uppercase tracking-wider text-primary">{lang === "es" ? "Cobertura" : "Coverage"}</p>
                    <p className="text-sm font-bold mt-0.5">{lang === "es" ? "Activa" : "Active"}</p>
                  </div>
                  <div className="rounded-xl bg-secondary/40 p-3 text-center">
                    <p className="text-xs font-bold uppercase tracking-wider">{lang === "es" ? "Servicios" : "Services"}</p>
                    <p className="text-sm font-bold mt-0.5">{lang === "es" ? "Todos" : "All"}</p>
                  </div>
                  <div className="rounded-xl bg-accent/30 p-3 text-center">
                    <p className="text-xs font-bold uppercase tracking-wider">{lang === "es" ? "Match" : "Match"}</p>
                    <p className="text-sm font-bold mt-0.5">~24h</p>
                  </div>
                </div>
                <Button
                  className="rounded-full font-bold w-full mt-3"
                  onClick={() => {
                    setCityModal(null);
                    navigate("/request-training");
                  }}
                  data-testid="city-modal-cta"
                >
                  {lang === "es" ? `Solicitar en ${cityModal}` : `Request in ${cityModal}`}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
